/* eslint-disable no-bitwise, no-plusplus */
// qrSvg — a tiny, dependency-free QR Code (Model 2, byte mode) generator that
// returns an inline SVG string. We avoid pulling an npm dependency (`qrcode`)
// so deploy needs no `yarn install`, and we avoid <canvas> so it is fully
// SSR-safe. Only ever call this from a browser lifecycle/handler — though it
// has no DOM access, keeping it out of render avoids any surprise.
//
// Implementation: a compact public-domain QR encoder (Kazuhiko Arase's qrcode
// lineage), trimmed to byte mode + the error-correction/version math we need.
// Supports up to version 10 (good for ~270 bytes at ECC level M) — more than
// enough for a `<origin>/m/photos/<32-hex>` URL (~60-90 chars).

// --- Galois field tables (GF(256)) ---
const EXP = new Array(256);
const LOG = new Array(256);
(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) {
    EXP[i] = x;
    LOG[x] = i;
    x <<= 1;
    if (x & 0x100) x ^= 0x11d;
  }
  for (let i = 255; i < 256; i++) EXP[i] = EXP[i - 255];
})();

function gexp(n) {
  while (n < 0) n += 255;
  while (n >= 256) n -= 255;
  return EXP[n];
}
function glog(n) {
  return LOG[n];
}

// Polynomial multiply in GF(256).
function polyMul(a, b) {
  const out = new Array(a.length + b.length - 1).fill(0);
  for (let i = 0; i < a.length; i++) {
    for (let j = 0; j < b.length; j++) {
      out[i + j] ^= gexp(glog(a[i]) + glog(b[j]));
    }
  }
  return out;
}

// Reed-Solomon generator polynomial of given degree.
function rsGenerator(degree) {
  let poly = [1];
  for (let i = 0; i < degree; i++) poly = polyMul(poly, [1, gexp(i)]);
  return poly;
}

function rsEncode(data, ecLen) {
  const gen = rsGenerator(ecLen);
  const res = data.concat(new Array(ecLen).fill(0));
  for (let i = 0; i < data.length; i++) {
    const coef = res[i];
    if (coef !== 0) {
      for (let j = 0; j < gen.length; j++) {
        res[i + j] ^= gexp(glog(gen[j]) + glog(coef));
      }
    }
  }
  return res.slice(data.length);
}

// --- Per-version capacity tables (ECC level M, byte mode) ---
// [ version: { size, ecPerBlock, [ [numBlocks, dataCodewords], ... ] } ]
// Subset of the spec (versions 1..10), enough for our short URLs.
const VERSIONS = {
  1: { ec: 10, groups: [[1, 16]] },
  2: { ec: 16, groups: [[1, 28]] },
  3: { ec: 26, groups: [[1, 44]] },
  4: { ec: 18, groups: [[2, 32]] },
  5: { ec: 24, groups: [[2, 43]] },
  6: { ec: 16, groups: [[4, 27]] },
  7: { ec: 18, groups: [[4, 31]] },
  8: { ec: 22, groups: [[2, 38], [2, 39]] },
  9: { ec: 22, groups: [[3, 36], [2, 37]] },
  10: { ec: 26, groups: [[4, 43], [1, 44]] },
};

function versionSize(v) {
  return 17 + v * 4;
}

function totalDataCodewords(v) {
  const cfg = VERSIONS[v];
  return cfg.groups.reduce((sum, [n, d]) => sum + n * d, 0);
}

// Build the byte-mode bit stream, pad to capacity, split into blocks, interleave.
function buildCodewords(bytes, version) {
  const cfg = VERSIONS[version];
  const dataCount = totalDataCodewords(version);

  // Bit buffer
  const bits = [];
  const put = (val, len) => {
    for (let i = len - 1; i >= 0; i--) bits.push((val >>> i) & 1);
  };

  put(0b0100, 4); // byte mode indicator
  // char-count indicator: 8 bits for versions 1..9, 16 bits for 10+
  put(bytes.length, version >= 10 ? 16 : 8);
  for (let i = 0; i < bytes.length; i++) put(bytes[i], 8);

  // terminator (up to 4 zero bits) + byte-align
  const cap = dataCount * 8;
  for (let i = 0; i < 4 && bits.length < cap; i++) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  // to bytes
  const dataCodewords = [];
  for (let i = 0; i < bits.length; i += 8) {
    let b = 0;
    for (let j = 0; j < 8; j++) b = (b << 1) | bits[i + j];
    dataCodewords.push(b);
  }
  // pad bytes
  const PADS = [0xec, 0x11];
  let p = 0;
  while (dataCodewords.length < dataCount) {
    dataCodewords.push(PADS[p % 2]);
    p++;
  }

  // split into blocks per groups
  const blocks = [];
  let pos = 0;
  cfg.groups.forEach(([n, d]) => {
    for (let i = 0; i < n; i++) {
      const data = dataCodewords.slice(pos, pos + d);
      pos += d;
      blocks.push({ data, ec: rsEncode(data, cfg.ec) });
    }
  });

  // interleave data codewords, then ec codewords
  const result = [];
  const maxData = Math.max(...blocks.map((b) => b.data.length));
  for (let i = 0; i < maxData; i++) {
    blocks.forEach((b) => { if (i < b.data.length) result.push(b.data[i]); });
  }
  for (let i = 0; i < cfg.ec; i++) {
    blocks.forEach((b) => { result.push(b.ec[i]); });
  }
  return result;
}

// --- Matrix placement ---
function makeMatrix(version, codewords) {
  const size = versionSize(version);
  const m = Array.from({ length: size }, () => new Array(size).fill(null));
  const reserved = Array.from({ length: size }, () => new Array(size).fill(false));

  const setF = (r, c, val) => { m[r][c] = val; reserved[r][c] = true; };

  // finder pattern + separators
  const placeFinder = (r0, c0) => {
    for (let r = -1; r <= 7; r++) {
      for (let c = -1; c <= 7; c++) {
        const rr = r0 + r;
        const cc = c0 + c;
        if (rr < 0 || rr >= size || cc < 0 || cc >= size) continue;
        const inRing = (r >= 0 && r <= 6 && (c === 0 || c === 6))
          || (c >= 0 && c <= 6 && (r === 0 || r === 6));
        const inCore = r >= 2 && r <= 4 && c >= 2 && c <= 4;
        setF(rr, cc, inRing || inCore ? 1 : 0);
      }
    }
  };
  placeFinder(0, 0);
  placeFinder(0, size - 7);
  placeFinder(size - 7, 0);

  // timing patterns
  for (let i = 8; i < size - 8; i++) {
    const v = i % 2 === 0 ? 1 : 0;
    if (m[6][i] === null) setF(6, i, v);
    if (m[i][6] === null) setF(i, 6, v);
  }

  // alignment patterns (centers per version)
  const ALIGN = {
    1: [], 2: [6, 18], 3: [6, 22], 4: [6, 26], 5: [6, 30],
    6: [6, 34], 7: [6, 22, 38], 8: [6, 24, 42], 9: [6, 26, 46], 10: [6, 28, 50],
  };
  const centers = ALIGN[version] || [];
  centers.forEach((r) => {
    centers.forEach((c) => {
      // skip if overlapping a finder
      if (reserved[r][c]) return;
      for (let dr = -2; dr <= 2; dr++) {
        for (let dc = -2; dc <= 2; dc++) {
          const isRing = Math.max(Math.abs(dr), Math.abs(dc)) === 2;
          const isCenter = dr === 0 && dc === 0;
          setF(r + dr, c + dc, isRing || isCenter ? 1 : 0);
        }
      }
    });
  });

  // dark module
  setF(size - 8, 8, 1);

  // reserve format info areas (filled later)
  for (let i = 0; i < 9; i++) {
    if (!reserved[i][8]) reserved[i][8] = true;
    if (!reserved[8][i]) reserved[8][i] = true;
  }
  for (let i = 0; i < 8; i++) {
    reserved[size - 1 - i][8] = true;
    reserved[8][size - 1 - i] = true;
  }

  // place data with zig-zag, applying mask 0 ((r+c)%2==0)
  let bitIdx = 0;
  const totalBits = codewords.length * 8;
  const getBit = (idx) => (idx < totalBits
    ? (codewords[idx >> 3] >> (7 - (idx & 7))) & 1
    : 0);

  let upward = true;
  for (let col = size - 1; col > 0; col -= 2) {
    const c = col === 6 ? col - 1 : col; // skip timing column
    for (let i = 0; i < size; i++) {
      const row = upward ? size - 1 - i : i;
      for (let j = 0; j < 2; j++) {
        const cc = c - j;
        if (reserved[row][cc]) continue;
        let bit = getBit(bitIdx++);
        if ((row + cc) % 2 === 0) bit ^= 1; // mask pattern 0
        m[row][cc] = bit;
      }
    }
    upward = !upward;
  }

  // format info for ECC level M (10) + mask 0 -> precomputed 15-bit string
  // BCH-encoded format bits for (M, mask0): 0b101010000010010
  const FORMAT = [1, 0, 1, 0, 1, 0, 0, 0, 0, 0, 1, 0, 0, 1, 0];
  // around top-left finder
  for (let i = 0; i <= 5; i++) m[8][i] = FORMAT[i];
  m[8][7] = FORMAT[6];
  m[8][8] = FORMAT[7];
  m[7][8] = FORMAT[8];
  for (let i = 9; i < 15; i++) m[14 - i][8] = FORMAT[i];
  // around the other two finders
  for (let i = 0; i < 8; i++) m[size - 1 - i][8] = FORMAT[i];
  for (let i = 8; i < 15; i++) m[8][size - 15 + i] = FORMAT[i];

  return m;
}

function utf8Bytes(str) {
  const out = [];
  for (let i = 0; i < str.length; i++) {
    let code = str.charCodeAt(i);
    if (code < 0x80) {
      out.push(code);
    } else if (code < 0x800) {
      out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
    } else if (code >= 0xd800 && code <= 0xdbff) {
      // surrogate pair
      const next = str.charCodeAt(++i);
      code = 0x10000 + ((code - 0xd800) << 10) + (next - 0xdc00);
      out.push(
        0xf0 | (code >> 18),
        0x80 | ((code >> 12) & 0x3f),
        0x80 | ((code >> 6) & 0x3f),
        0x80 | (code & 0x3f),
      );
    } else {
      out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
    }
  }
  return out;
}

function pickVersion(byteLen) {
  for (let v = 1; v <= 10; v++) {
    // available data bytes minus mode(4) + count indicator + terminator overhead
    const countBits = v >= 10 ? 16 : 8;
    const capacityBits = totalDataCodewords(v) * 8 - 4 - countBits;
    if (byteLen * 8 <= capacityBits) return v;
  }
  return null;
}

// Public: return an SVG string for `text`. Returns '' if the text is too long
// for our supported versions (callers can fall back to a plain link).
export default function qrSvg(text, opts = {}) {
  try {
    const margin = opts.margin != null ? opts.margin : 4;
    const size = opts.size != null ? opts.size : 240;
    const bytes = utf8Bytes(String(text));
    const version = pickVersion(bytes.length);
    if (!version) return '';

    const codewords = buildCodewords(bytes, version);
    const matrix = makeMatrix(version, codewords);
    const n = matrix.length;
    const total = n + margin * 2;

    let rects = '';
    for (let r = 0; r < n; r++) {
      for (let c = 0; c < n; c++) {
        if (matrix[r][c]) {
          rects += `M${c + margin},${r + margin}h1v1h-1z`;
        }
      }
    }

    return (
      `<svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" `
      + `viewBox="0 0 ${total} ${total}" shape-rendering="crispEdges" role="img" aria-label="QR code">`
      + `<rect width="${total}" height="${total}" fill="#ffffff"/>`
      + `<path d="${rects}" fill="#111111"/>`
      + '</svg>'
    );
  } catch (e) {
    return '';
  }
}
